import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/photobooth`;

export default function TemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [template, setTemplate] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [draggedImage, setDraggedImage] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 850, height: 1100 });

  useEffect(() => {
    // Adjust canvas size for single photo templates
    if (template?.template_type === "single") {
      setCanvasSize({ width: 350, height: 550 }); // 3.5" x 5.5" portrait at 100 pixels per inch
    } else {
      setCanvasSize({ width: 850, height: 1100 }); // 8.5" x 11"
    }
  }, [template?.template_type]);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [uploadingToSlot, setUploadingToSlot] = useState(null);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [applyingBulk, setApplyingBulk] = useState(false);
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState({});

  const SCALE = 100; // 100 pixels = 1 inch

  useEffect(() => {
    fetchTemplate();
    fetchImages();
  }, [templateId]);

  useEffect(() => {
    if (template) {
      drawCanvas();
    }
  }, [template, images, selectedImage, hoveredSlot, loadedImages]);

  useEffect(() => {
    // Load images
    images.forEach((img) => {
      if (!loadedImages[img.id]) {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
          setLoadedImages(prev => ({ ...prev, [img.id]: image }));
        };
        image.src = `${API}/images/${img.id}`;
      }
    });
  }, [images]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}`);
      setTemplate(response.data);
    } catch (error) {
      toast.error("Failed to load template");
      navigate("/");
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/images`);
      setImages(response.data);
    } catch (error) {
      toast.error("Failed to load images");
    }
  };

  const getShapePositions = () => {
    if (!template) return [];
    
    if (template.template_type === "single") {
      // Single photo fills entire 3.5" x 5.5" portrait page
      const rectWidth = 3.5 * SCALE;
      const rectHeight = 5.5 * SCALE;
      
      return [{
        index: 0,
        x: 0,
        y: 0,
        width: rectWidth,
        height: rectHeight,
        type: "rectangle"
      }];
    } else if (template.template_type === "rectangle") {
      // 3.5" x 5.5" rectangles, 2x2 grid
      const rectWidth = 3.5 * SCALE;
      const rectHeight = 5.5 * SCALE;
      const totalWidth = 2 * rectWidth;
      const totalHeight = 2 * rectHeight;
      const marginX = (8.5 * SCALE - totalWidth) / 2;
      const marginY = (11 * SCALE - totalHeight) / 2;
      
      const positions = [];
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 2; col++) {
          positions.push({
            index: row * 2 + col,
            x: marginX + col * rectWidth,
            y: marginY + row * rectHeight,
            width: rectWidth,
            height: rectHeight,
            type: "rectangle"
          });
        }
      }
      return positions;
    } else {
      // 1.97" circles
      const circleDiameter = 1.97 * SCALE;
      const margin = 0.4 * SCALE;
      const availableWidth = 8.5 * SCALE - 2 * margin;
      const availableHeight = 11 * SCALE - 2 * margin;
      
      const cols = Math.floor(availableWidth / circleDiameter);
      const rows = Math.floor(availableHeight / circleDiameter);
      
      const spacingX = (availableWidth - cols * circleDiameter) / (cols + 1);
      const spacingY = (availableHeight - rows * circleDiameter) / (rows + 1);
      
      const positions = [];
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          positions.push({
            index: row * cols + col,
            x: margin + spacingX + col * (circleDiameter + spacingX),
            y: margin + spacingY + row * (circleDiameter + spacingY),
            width: circleDiameter,
            height: circleDiameter,
            type: "circle"
          });
        }
      }
      return positions;
    }
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw page background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw shapes (no borders for single photo templates)
    const positions = getShapePositions();
    
    positions.forEach((pos) => {
      // Only draw borders for non-single templates
      if (template.template_type !== "single") {
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = 2;
        
        if (pos.type === "rectangle") {
          ctx.strokeRect(pos.x, pos.y, pos.width, pos.height);
        } else {
          const centerX = pos.x + pos.width / 2;
          const centerY = pos.y + pos.height / 2;
          const radius = pos.width / 2;
          
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          ctx.stroke();
        }

        // Draw slot number
        ctx.fillStyle = "#666666";
        ctx.font = "12px Inter";
        ctx.textAlign = "center";
        ctx.fillText(`#${pos.index + 1}`, pos.x + pos.width / 2, pos.y + 20);
      }
    });

    // Draw slots and images
    positions.forEach((pos) => {
      const img = images.find(i => i.circle_index === pos.index);
      const isHovered = hoveredSlot === pos.index;
      
      if (img) {
        // Has image - draw it
        const loadedImg = loadedImages[img.id];
        
        if (loadedImg) {
          ctx.save();
          
          // Create clipping path
          ctx.beginPath();
          if (pos.type === "rectangle") {
            ctx.rect(pos.x, pos.y, pos.width, pos.height);
          } else {
            ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
          }
          ctx.clip();
          
          // Draw image
          const scale = Math.max(pos.width / loadedImg.width, pos.height / loadedImg.height);
          const imgWidth = loadedImg.width * scale;
          const imgHeight = loadedImg.height * scale;
          const imgX = pos.x + (pos.width - imgWidth) / 2;
          const imgY = pos.y + (pos.height - imgHeight) / 2;
          
          ctx.drawImage(loadedImg, imgX, imgY, imgWidth, imgHeight);
          ctx.restore();
        } else {
          // Loading placeholder
          ctx.fillStyle = "#e5e7eb";
          if (pos.type === "rectangle") {
            ctx.fillRect(pos.x, pos.y, pos.width, pos.height);
          } else {
            ctx.beginPath();
            ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
            ctx.fill();
          }
          
          ctx.fillStyle = "#9ca3af";
          ctx.font = "12px Inter";
          ctx.textAlign = "center";
          ctx.fillText("Loading...", pos.x + pos.width / 2, pos.y + pos.height / 2);
        }
        
        // Highlight if selected
        if (selectedImage && selectedImage.id === img.id) {
          ctx.strokeStyle = "#8b5cf6";
          ctx.lineWidth = 4;
          if (pos.type === "rectangle") {
            ctx.strokeRect(pos.x, pos.y, pos.width, pos.height);
          } else {
            ctx.beginPath();
            ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
            ctx.stroke();
          }
        }
      } else {
        // Empty slot - show + button on hover
        if (isHovered) {
          ctx.fillStyle = "rgba(139, 92, 246, 0.1)";
          if (pos.type === "rectangle") {
            ctx.fillRect(pos.x, pos.y, pos.width, pos.height);
          } else {
            ctx.beginPath();
            ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
            ctx.fill();
          }
        }
        
        // Draw + icon
        const centerX = pos.x + pos.width / 2;
        const centerY = pos.y + pos.height / 2;
        const plusSize = isHovered ? 30 : 20;
        
        ctx.strokeStyle = isHovered ? "#8b5cf6" : "#d1d5db";
        ctx.lineWidth = isHovered ? 4 : 3;
        ctx.lineCap = "round";
        
        // Vertical line
        ctx.beginPath();
        ctx.moveTo(centerX, centerY - plusSize / 2);
        ctx.lineTo(centerX, centerY + plusSize / 2);
        ctx.stroke();
        
        // Horizontal line
        ctx.beginPath();
        ctx.moveTo(centerX - plusSize / 2, centerY);
        ctx.lineTo(centerX + plusSize / 2, centerY);
        ctx.stroke();
        
        // "Add Image" text on hover
        if (isHovered) {
          ctx.fillStyle = "#8b5cf6";
          ctx.font = "bold 12px Inter";
          ctx.textAlign = "center";
          ctx.fillText("Add Image", centerX, centerY + plusSize + 15);
        }
      }
    });
  };

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    if (!clientX || !clientY) return;
    
    // Scale coordinates for canvas size vs display size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    const positions = getShapePositions();
    
    // Check if clicked on an image or slot
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const img = images.find(img => img.circle_index === i);
      
      let isInside = false;
      
      if (pos.type === "rectangle") {
        isInside = x >= pos.x && x <= pos.x + pos.width && y >= pos.y && y <= pos.y + pos.height;
      } else {
        const centerX = pos.x + pos.width / 2;
        const centerY = pos.y + pos.height / 2;
        const radius = pos.width / 2;
        const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
        isInside = distance <= radius;
      }
      
      if (isInside) {
        if (img) {
          setSelectedImage(img);
        } else {
          // Empty slot - trigger upload
          setUploadingToSlot(i);
          fileInputRef.current?.click();
        }
        return;
      }
    }

    // Clicked on empty space
    setSelectedImage(null);
  };

  const handleCanvasMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const positions = getShapePositions();
    
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      let isInside = false;
      
      if (pos.type === "rectangle") {
        isInside = x >= pos.x && x <= pos.x + pos.width && y >= pos.y && y <= pos.y + pos.height;
      } else {
        const centerX = pos.x + pos.width / 2;
        const centerY = pos.y + pos.height / 2;
        const radius = pos.width / 2;
        const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
        isInside = distance <= radius;
      }
      
      if (isInside) {
        setHoveredSlot(i);
        return;
      }
    }
    
    setHoveredSlot(null);
  };

  const deleteImage = async () => {
    if (!selectedImage) return;

    if (!window.confirm("Delete this image?")) return;

    try {
      await axios.delete(`${API}/images/${selectedImage.id}`);
      toast.success("Image deleted");
      setSelectedImage(null);
      fetchTemplate();
      fetchImages();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const moveImageToSlot = async (newIndex) => {
    if (!selectedImage) return;

    try {
      await axios.put(`${API}/templates/${templateId}/move-image`, {
        image_id: selectedImage.id,
        new_circle_index: newIndex,
      });
      toast.success("Image moved");
      fetchImages();
    } catch (error) {
      toast.error("Failed to move image");
    }
  };

  const uploadImageToSlot = async (file, slotIndex) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/templates/${templateId}/upload-owner`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // Move to specific slot if needed
      if (slotIndex !== null && slotIndex !== undefined) {
        const imageId = response.data.image_id;
        await axios.put(`${API}/templates/${templateId}/move-image`, {
          image_id: imageId,
          new_circle_index: slotIndex,
        });
      }
      
      toast.success("Image added");
      fetchTemplate();
      fetchImages();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Invalid image format. Use JPG, PNG, or HEIC.");
      } else if (error.response?.status === 507) {
        toast.error("Out of memory. Try a smaller image (under 5MB).");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to upload image");
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file && uploadingToSlot !== null) {
      await uploadImageToSlot(file, uploadingToSlot);
      setUploadingToSlot(null);
    }
    e.target.value = ""; // Reset input
  };

  const applyImageToAllSlots = async () => {
    if (!bulkUploadFile) {
      toast.error("Please select an image first");
      return;
    }

    const emptySlots = [];
    for (let i = 0; i < template.total_slots; i++) {
      if (!images.find(img => img.circle_index === i)) {
        emptySlots.push(i);
      }
    }

    if (emptySlots.length === 0) {
      toast.error("All slots are already filled. Delete some images first.");
      return;
    }

    if (!window.confirm(`Apply this image to ${emptySlots.length} empty slots?`)) {
      return;
    }

    setApplyingBulk(true);

    try {
      const formData = new FormData();
      formData.append("file", bulkUploadFile);
      
      const response = await axios.post(`${API}/templates/${templateId}/bulk-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      toast.success(`✅ ${response.data.filled_slots} slots filled in one go!`);
      
      setBulkUploadFile(null);
      fetchTemplate();
      fetchImages();
    } catch (error) {
      console.error("Bulk upload error:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Invalid image format. Use JPG, PNG, or HEIC.");
      } else if (error.response?.status === 507) {
        toast.error("Out of memory. Try a smaller image (under 5MB).");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to apply to slots. Check image format (JPG, PNG, HEIC).");
      }
    } finally {
      setApplyingBulk(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/pdf`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${template.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  if (!template) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {template.name}
              </h1>
              <p className="text-gray-600">
                {template.template_type === "single" 
                  ? "Single Photo (3.5\"×5.5\")"
                  : template.template_type === "rectangle" 
                  ? "Rectangle Template" 
                  : "Circle Template"
                } •{" "}
                {template.filled_count} / {template.total_slots} filled
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            {selectedImage && (
              <Button
                variant="destructive"
                onClick={deleteImage}
                data-testid="delete-selected-image-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
            )}
            <Button
              onClick={downloadPDF}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
              data-testid="download-pdf-editor-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-2xl p-6 border-2 border-gray-200">
              <div className="mb-4">
                <h2 className="text-lg font-semibold mb-2">Template Preview</h2>
                <p className="text-sm text-gray-600">
                  Fixed borders show print positions. Click images to select, use controls to move/delete. High-quality print output.
                </p>
              </div>
              <div className="border-4 border-gray-300 rounded-lg overflow-auto bg-gray-100 p-4">
                <canvas
                  ref={canvasRef}
                  width={canvasSize.width}
                  height={canvasSize.height}
                  onClick={handleCanvasClick}
                  onTouchEnd={handleCanvasClick}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseLeave={() => setHoveredSlot(null)}
                  className="bg-white shadow-lg cursor-pointer mx-auto"
                  data-testid="template-canvas"
                  style={{ maxWidth: '100%', height: 'auto', touchAction: 'none' }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="slot-file-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-xl p-6 border-2 border-gray-200">
              <h2 className="text-lg font-semibold mb-4">Bulk Upload</h2>
              <p className="text-sm text-gray-600 mb-4">
                Upload one image to fill all empty slots. Supports JPG, PNG, HEIC. Images keep original backgrounds.
              </p>
              <div className="space-y-3">
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setBulkUploadFile(e.target.files[0])}
                  className="w-full text-sm border rounded px-3 py-2"
                  data-testid="bulk-upload-input"
                />
                {bulkUploadFile && (
                  <div className="text-sm text-gray-600">
                    Selected: {bulkUploadFile.name}
                  </div>
                )}
                <Button
                  onClick={applyImageToAllSlots}
                  disabled={!bulkUploadFile || applyingBulk}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                  data-testid="apply-to-all-btn"
                >
                  {applyingBulk ? "Applying..." : "Apply to Empty Slots"}
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-xl p-6 border-2 border-gray-200">
              <h2 className="text-lg font-semibold mb-4">Selected Image</h2>
              {selectedImage ? (
                <div className="space-y-4">
                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-purple-500">
                    <img
                      src={`${API}/images/${selectedImage.id}`}
                      alt="Selected"
                      className="w-full h-full object-cover"
                      data-testid="selected-image-preview"
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Current position: Slot #{selectedImage.circle_index + 1}
                    </p>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Move to slot:</label>
                      <select
                        className="w-full border rounded px-3 py-2"
                        value={selectedImage.circle_index}
                        onChange={(e) => moveImageToSlot(parseInt(e.target.value))}
                        data-testid="move-image-select"
                      >
                        {Array.from({ length: template.total_slots }, (_, i) => (
                          <option key={i} value={i}>
                            Slot #{i + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={deleteImage}
                      className="w-full"
                      data-testid="delete-image-sidebar-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Image
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <ImageIcon className="w-16 h-16 mx-auto mb-3 opacity-50" />
                  <p>Click an image to select</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-xl p-6 border-2 border-gray-200">
              <h2 className="text-lg font-semibold mb-4">All Images ({images.length})</h2>
              <div className="grid grid-cols-3 gap-2 max-h-96 overflow-y-auto">
                {images.map((img) => (
                  <div
                    key={img.id}
                    onClick={() => setSelectedImage(img)}
                    className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      selectedImage?.id === img.id
                        ? "border-purple-500 ring-2 ring-purple-300"
                        : "border-gray-200 hover:border-purple-300"
                    }`}
                    data-testid={`thumbnail-${img.id}`}
                  >
                    <img
                      src={`${API}/images/${img.id}`}
                      alt={`Slot ${img.circle_index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              {images.length === 0 && (
                <p className="text-center text-gray-400 py-8">No images yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ImageIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);
